import { SkeletonSpinner, SkeletonSpinnerCentered } from '@civilgrid/ui/SkeletonLoader';
import { Link, useParams } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import * as MapGL from 'react-map-gl/mapbox';
import { useSnapshot } from 'valtio';
import { subscribeKey } from 'valtio/utils';
import { getProject as apiGetProject, getIntelligenceFeatures } from '~/api/projects2';
import { capturePosthogEvent, captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import Icon from '~/components2/Icon';
import { useOwnMapClicks } from '~/components2/Map/mapClickOwnership';
import { measureStatus } from '~/components2/Map/Widgets/Measure/MeasureSurface';
import { useIsMounted } from '~/hooks/useIsMounted';
import rootStore from '~/store';
import { useTabControlledLayers } from '~/store2/tabLayerLocks';
import type { ProjectDetailsParams } from '..';
import type { TIntelligenceQuery } from './common';
import { layersToSkipRender } from './common/config';
import { layerIdMap } from './layers/common';
// map children/layers imports
import FeatureLayers from './layers/FeatureLayers';
import HoverLayer from './layers/HoverLayer';
import SelectedLayer from './layers/SelectedLayer';
import JurisdicitonalIntersections from './sections/JurisdicitonalIntersections';
// sections
import NearbyProjects from './sections/NearbyProjects';
import PhysicalInfrastructure from './sections/PhysicalInfrastructure';
import RightOfWayAndCrossings from './sections/RightOfWayAndCrossings';
import store from './store';

export function MapChildren() {
  // This tab uses left-click to select features, so claim map-click ownership (CG-670 sidebar defers).
  useOwnMapClicks();
  const { current: map } = MapGL.useMap();
  const { config, projectIntelligence, summary } = useSnapshot(store) as typeof store;

  async function onClick(e: MapGL.MapMouseEvent) {
    if (measureStatus.isMeasuring === true) return;

    const findFeature = () => {
      const queriedFeatures = map?.queryRenderedFeatures(e.point);

      let foundFeature: GeoJSON.Feature | undefined;

      const intelFeatureIds = Object.keys(projectIntelligence);
      const feat = queriedFeatures?.find(
        (f) => f.layer?.source?.toString() && intelFeatureIds.includes(f.layer.source.toString()),
      );

      if (feat?.layer?.source?.toString()) {
        const featureId = feat.layer.source?.toString();
        const layerId = projectIntelligence[featureId]?.layerId;

        if (layerId) {
          const layer = config[layerId];
          if (layer) {
            const match = layer[featureId];
            if (match) {
              foundFeature = match;
            }
          }
        }
      }

      return foundFeature;
    };

    if (map) {
      const foundFeature = findFeature();

      if (foundFeature) {
        store.setSelectedFeature(foundFeature);
        capturePosthogEvent(POSTHOG_EVENTS.intelligenceFeatureSelected, {
          project_id: store.project?.id,
          organization_id: rootStore.currentOrganizationId || undefined,
          accessed_from: 'map',
          feature_id: foundFeature.id,
        });
        // settimeout needed here so function doesn't "return" before map view change.
        setTimeout(() => store.zoomToFeature(foundFeature), 0);
        return;
      }
    }

    store.resetSelectedFeature();
  }

  function onKeyUp(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      if (store.selectedFeature) {
        store.resetSelectedFeature();
      }
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    store.setCurrentMap(map);
    map?.on('click', onClick);

    // for escape listener
    window.addEventListener('keyup', onKeyUp);

    const unsubscribe = subscribeKey(store, 'activeSection', () => {
      store.resetSelectedFeature();
      store.resetHoveredFeature();
    });

    return () => {
      map?.off('click', onClick);
      window.removeEventListener('keyup', onKeyUp);
      store.resetCurrentMap();
      unsubscribe();
    };
  }, [map, projectIntelligence, config]);

  if (summary?.computeStatus === 'processing' || summary?.computeStatus === 'error') {
    return null;
  }

  return (
    <>
      <FeatureLayers />
      <HoverLayer />
      <SelectedLayer />
    </>
  );
}

export function TabDetails() {
  const [isMissingDates, setIsMissingDates] = useState(false);
  const { projectId } = useParams({ strict: false }) as ProjectDetailsParams;
  const { isLoading, isError, project, activeSection, summary } = useSnapshot(store) as typeof store;
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMounted = useIsMounted();

  // PostHog tab-viewed capture; fires once per project mount.
  useEffect(() => {
    if (!projectId) return;
    captureTypedPosthogEvent(POSTHOG_EVENTS.projectIntelligenceTabViewed, {
      project_id: projectId,
      organization_id: rootStore.currentOrganizationId || undefined,
      tab_id: 'intelligence',
    });
  }, [projectId]);

  const removeTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const fetchData = async (shouldFetchProject: boolean): Promise<{ success: boolean }> => {
    try {
      if (shouldFetchProject === true) {
        const project = await apiGetProject(projectId);
        store.setProject(project);

        if (!project.constructionStartDate || !project.constructionEndDate) {
          setIsMissingDates(true);
          return { success: true };
        }
      }

      const res = await getIntelligenceFeatures<TIntelligenceQuery>(projectId);

      if (res.summary) {
        store.summary = res.summary;
      }

      if (res?.summary?.computeStatus === 'valid') {
        removeTimeout();
        if (isMounted()) {
          store.formatData(projectId, res);
        }
        return { success: true };
      } else if (res?.summary?.computeStatus === 'error') {
        removeTimeout();
        return { success: true };
      } else {
        return await new Promise(() => {
          timeoutRef.current = setTimeout(() => fetchData(false), 3_000);
        });
      }
    } catch {
      return { success: false };
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    store.setup();
    fetchData(true);

    return () => {
      store.reset();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  // The active section's ids are forced on ADDITIVELY (the user's other layers stay on,
  // same behavior as the Land Rights / Zoning tabs) and tab-locked, so the picker shows
  // them checked + disabled instead of fighting the tab. A section switch or leaving the
  // tab returns the previous section's ids to picker-controlled state.
  const forcedSectionLayerIds = useMemo(
    () => (layerIdMap[activeSection] || []).filter((id) => !layersToSkipRender.includes(id)),
    [activeSection],
  );
  useTabControlledLayers(forcedSectionLayerIds);

  // Fires once per failed summary — keyed on summary id so it doesn't spam on every render.
  useEffect(() => {
    if (summary?.computeStatus !== 'error' || !summary?.id) return;

    console.error('Intelligence compute failed', { projectId, summaryId: summary.id });
    capturePosthogEvent(POSTHOG_EVENTS.intelligenceComputeFailed, {
      project_id: projectId,
      organization_id: rootStore.currentOrganizationId || undefined,
      summary_id: summary.id,
    });
  }, [summary?.computeStatus, summary?.id, projectId]);

  if (isMissingDates) {
    return (
      <div className="text-sm">
        Please select construction start and end dates to access project intelligence. You can do this in the{' '}
        <Link
          className="text-blue-50 underline hover:text-blue-60"
          to="/projects/$projectId/$tabId"
          params={{ projectId, tabId: 'details' }}
        >
          Project Info
        </Link>{' '}
        tab.
      </div>
    );
  }

  if (summary?.computeStatus === 'processing') {
    return (
      <div className="flex gap-2 text-sm">
        <SkeletonSpinner className="self-center" size={20} />
        <span>
          Intelligence is currently being computed for this project. Depending on the size of the project, it can take
          up to 15 minutes to complete.
        </span>
      </div>
    );
  }

  if (summary?.computeStatus === 'error') {
    return (
      <div className="flex gap-2 text-sm" data-testid="intelligence-summary-error">
        <Icon icon="warning" className="self-center" />
        <span>Intelligence failed to refresh for this project. We're looking into it.</span>
      </div>
    );
  }

  if (isLoading) {
    return <SkeletonSpinnerCentered />;
  }

  // this is a fetch error, not compute error from back end
  if (isError) {
    return <div className="text-sm">Failed to fetch project intelligence.</div>;
  }

  return (
    <div className="flex flex-col space-y-4">
      <NearbyProjects />
      <RightOfWayAndCrossings />
      <JurisdicitonalIntersections />
      <PhysicalInfrastructure />
    </div>
  );
}
